import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useStore } from '../../shared/contexts/StoreContext';
import { OrderService } from '../../shared/services/OrderService';
import { useToast } from '../../shared/contexts/ToastContext';

const Container = styled.div`
  max-width: 450px;
  margin: 0 auto;
  padding: 20px;
  background: #f9fafb;
  min-height: 100vh;
  box-sizing: border-box;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  color: #1e293b;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  background: ${props => props.status === 'open' ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.status === 'open' ? '#166534' : '#991b1b'};
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`;

const ItemBlock = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
      border-bottom: none;
  }
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`;

const ItemName = styled.div`
  font-weight: 700;
  color: #334155;
  font-size: 1rem;
`;

const ItemPrice = styled.div`
  font-weight: 800;
  color: #ea580c;
  font-size: 1rem;
`;

const OptionsText = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  margin-left: 0;
  font-style: italic;
`;

const TotalSection = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px dashed #e2e8f0;
`;

const TotalRow = styled.div<{ highlight?: boolean }>`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: ${props => props.highlight ? '900' : '600'};
  font-size: ${props => props.highlight ? '1.35rem' : '1rem'};
  color: ${props => props.highlight ? '#1e293b' : '#475569'};
`;

const Button = styled.button`
  width: 100%;
  padding: 18px;
  border-radius: 14px;
  border: none;
  background: #ea580c;
  color: white;
  font-weight: 800;
  font-size: 1.1rem;
  cursor: pointer;
  margin-top: 10px;
  box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  transition: all 0.2s;
  &:active {
      transform: scale(0.98);
  }
`;

const POLL_INTERVAL_MS = 15000;

export const ComandaPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { store } = useStore();
    const { showToast } = useToast();
    const [comanda, setComanda] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Parâmetros suportados:
    // ?telefone=X&senha=Y — fluxo legado (garçom/table)
    // ?id=X&senha=Y       — comanda simples (cliente via QR da mesa)
    // ?cartao=X           — cartão comanda (cliente via QR do cartão)
    const phone = searchParams.get('telefone');
    const senha = searchParams.get('senha');
    const comandaId = searchParams.get('id');
    const cartao = searchParams.get('cartao');

    const isFirstLoad = useRef(true);

    const fetchComanda = useCallback(async () => {
        if (!store) return;

        try {
            let res: any;

            if (comandaId && senha) {
                res = await OrderService.getComandaByIdClient(comandaId, senha);
            } else if (cartao) {
                res = await OrderService.getComandaByCartao(store.id, cartao);
            } else if (phone && senha) {
                res = await OrderService.accessComanda({
                    storeId: store.id,
                    phone: phone as string,
                    password: senha,
                });
            } else {
                navigate('/');
                return;
            }

            if (res.success) {
                setComanda(res.comanda);
            }
        } catch (error: any) {
            if (isFirstLoad.current) {
                showToast(error.response?.data?.error || 'Erro ao carregar comanda', 'error');
                navigate('/');
            }
        } finally {
            isFirstLoad.current = false;
            setLoading(false);
        }
    }, [store, phone, senha, comandaId, cartao, navigate, showToast]);

    useEffect(() => {
        fetchComanda();
    }, [fetchComanda]);

    // Polling para atualizações em tempo real
    useEffect(() => {
        if (!store) return;
        pollingRef.current = setInterval(() => {
            fetchComanda();
        }, POLL_INTERVAL_MS);
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [store, fetchComanda]);

    const handleAddMore = () => {
        // Voltar ao menu preservando o parâmetro QR Code para continuar no modo comanda
        if (cartao) navigate(`/?cartao=${cartao}`);
        else if (comandaId) navigate(`/?mesa=${comanda?.mesaNumber || ''}`);
        else navigate('/');
    };

    const handleRefresh = () => { setLoading(true); fetchComanda(); };

    if (loading && !comanda) return <Container><p style={{ textAlign: 'center', paddingTop: '40px', color: '#64748b' }}>Carregando comanda...</p></Container>;
    if (!comanda) return <Container><p style={{ textAlign: 'center', paddingTop: '40px', color: '#64748b' }}>Comanda não encontrada.</p></Container>;

    return (
        <Container>
            <Card>
                <Header>
                    <Title>
                        {comanda.mesaNumber ? `Mesa ${comanda.mesaNumber}` : comanda.cardCode ? `Cartão #${comanda.cardCode}` : 'Comanda'}
                    </Title>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                        <StatusBadge status={comanda.status}>
                            {comanda.status === 'open' ? 'Aberta' :
                             comanda.status === 'partially_paid' ? 'Parcial' : 'Fechada'}
                        </StatusBadge>
                        <button onClick={handleRefresh} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.75rem', padding: 0 }}>
                            <i className="fa-solid fa-rotate-right" style={{ marginRight: '4px' }}></i>Atualizar
                        </button>
                    </div>
                </Header>

                {comanda.customerName && comanda.customerName !== 'Cliente' && (
                    <div style={{ marginBottom: '24px', background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', marginBottom: '4px' }}>Cliente</div>
                        <div style={{ fontWeight: '800', color: '#1e293b', fontSize: '1.1rem' }}>{comanda.customerName}</div>
                    </div>
                )}

                <div style={{ fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', marginBottom: '10px' }}>Consumo Detalhado</div>

                <ItemList>
                    {comanda.orders && comanda.orders.map((order: any, idx: number) => (
                        <React.Fragment key={idx}>
                            {order.items?.map((item: any, i: number) => (
                                <ItemBlock key={`${idx}-${i}`}>
                                    <ItemRow>
                                        <ItemName>{item.qty}x {item.name}</ItemName>
                                        <ItemPrice>R$ {(item.price * item.qty).toFixed(2)}</ItemPrice>
                                    </ItemRow>
                                    {(item.sauces?.length > 0 || item.sideDish) && (
                                        <OptionsText>
                                            {item.sauces?.length > 0 && `Molhos: ${item.sauces.map((s: string) => { const q = item.sauceQtys?.[s]; return q && q > 1 ? `${s} x${q}` : s; }).join(', ')}`}
                                            {item.sauces?.length > 0 && item.sideDish && ' | '}
                                            {item.sideDish && `Acomp.: ${item.sideDish}`}
                                        </OptionsText>
                                    )}
                                </ItemBlock>
                            ))}
                        </React.Fragment>
                    ))}
                    {(!comanda.orders || comanda.orders.length === 0) && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>Nenhum item consumido ainda.</div>
                    )}
                </ItemList>

                <TotalSection>
                    <TotalRow>
                        <span>Total Consumido</span>
                        <span>R$ {comanda.total.toFixed(2)}</span>
                    </TotalRow>
                    <TotalRow>
                        <span>Total Pago</span>
                        <span style={{ color: '#22c55e' }}>R$ {comanda.paidAmount.toFixed(2)}</span>
                    </TotalRow>
                    <TotalRow highlight>
                        <span>A Pagar</span>
                        <span style={{ color: '#ef4444' }}>R$ {(comanda.total - comanda.paidAmount).toFixed(2)}</span>
                    </TotalRow>
                </TotalSection>
            </Card>

            {comanda.status === 'open' && !store?.clienteNaoFazPedidos && (
                <Button onClick={handleAddMore}>
                    ADICIONAR MAIS ITENS
                </Button>
            )}

            <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '0.9rem', color: '#64748b', lineHeight: '1.5' }}>
                Para fechar sua comanda e realizar o pagamento, <br /><strong>chame o garçom.</strong>
            </p>
        </Container>
    );
};
